import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, icons } from "../../../Styles";
import { ProgressCircle, Last10DaysViewer, UNIT, FeedCurveWithLabels} from "../../";
import { Graph_QL, useFeedCurves, Location, MaintainAnimal, System, Consumption, useTerminals } from "../../../Services";
import { useAppSelector } from "../../../Store";
import PenList, { ListTypes } from "../../BaseComponents/Animals/penList";
import { selectFeedCurveById } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/root";
import { useNavigate } from "react-router";
import MotorSpecs from "../../motorSpecs";
import NewButton, { NewButtonList } from "../../BaseComponents/newButton";
import { ButtonTypes } from "../../../Definitions";
import AnimalNumberDisplay from "../../animalNumberDisplay";

interface penModal_Props {
  activePen?: Location;
  system: System
}

/**
 * PenModal
 * @param activePenId The pen number.
 * @returns A "div" to place in a modal to show all content from a Location Feeder Pen.
 */
export default function PenModal({ system, activePen }: penModal_Props) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const [currentFeedview, setCurrentFeedview] = useState<"LAST10DAYS" | "FEEDCURVE">("FEEDCURVE");
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const { call: getFeedCurves } = useFeedCurves(connection);
  const { call: getTerminals, data: terminals } = useTerminals(connection)  

  useEffect(() => {
    getFeedCurves({ fetchAmount: 50, ifFeedCurveBaseInfoOnly: false })
    getTerminals({ })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const terminalsForPen = useMemo(() => terminals[domain]?.data?.filter(
      terminal => terminal.fk_locations === activePen?.location_id
  ), [terminals, domain, activePen?.location_id]);

  console.log("terminalsInfo", terminalsForPen)

  const activeAnimal = useMemo(() => ({
    animal: activePen?.animals?.[0],
  }), [activePen]);

  const feedCurve = useAppSelector((state) =>  {if (activeAnimal.animal?.feedcurve_id === undefined) { return }; return selectFeedCurveById(state, activeAnimal.animal?.feedcurve_id)})

  // FeedCurve
  const FeedCurve: JSX.Element = useMemo(() => {

    if (!feedCurve) return (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
        {t("animalPages.animal.noFeedCurve", {defaultValue: "No Feed Curve Found"})}
      </p>
    )
    
    return (
      <FeedCurveWithLabels data={feedCurve} />
    )
  }, [feedCurve, t])

  // Last10Days
  const Last10Days: JSX.Element = useMemo(() => {
    const last10Days = activeAnimal.animal?.lastTenDay

    if (!last10Days) return (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
        {t("animalPages.animal.noFeedCurve", {defaultValue: "No Feed Curve Found"})}
      </p>
    )

    return (
      <Last10DaysViewer data={last10Days} />
    )
  }, [activeAnimal.animal?.lastTenDay, t])

  // #region | Feed View State Router
  const FeedviewStateRouter = () => {
    switch (currentFeedview) {
      case "LAST10DAYS":
        return (
            <>{Last10Days}</>
        );
      case "FEEDCURVE":
        return (
            <>{FeedCurve}</>
        );
      default:
        return null;
    }
  }
  // #endregion

  return ( <>
    <div style={{ ...css.modalTop, height: "200px", alignItems: "center"}}>
      
      <MotorSpecs terminals={terminalsForPen} farm={farm}/>

      <div style={{display: "flex"}}>
        <RemainingToday animalConsumption={activeAnimal.animal?.consumptions}/>
        <RemainingYesterday animalConsumption={activeAnimal.animal?.consumptions} />
      </div>
      
    </div>
    <div style={{...css.seperatorLine}}/>

    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center"}}>
      {activeAnimal.animal ? ( <>
        {/* Pen/PenEdit based on PenMode */}
          {activePen ?
            <PenList
              key={activePen.location_id}
              listType={ListTypes.animals}
              headerPen={true} systemType={system.fk_system_types}
              pens={[activePen]}
              onAnimalClick={(animalId: number) => RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animal, replace: true, navArgs: { selected: animalId }, currentDomain: farm })}
              connect={{ connection: connection, farm: farm, domain: domain }}
            />
          : <></>}

        <AnimalNumberDisplay
          content={t("locationFeederPages.sections.inThisPen", { defaultValue: "In this Pen" })}
          animal={activeAnimal.animal}
          style={{marginTop: "20px"}}
        />
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", padding: "0px 10px", borderTop: "1px solid "+colors.greyTertiary, borderBottom: "1px solid "+colors.greyTertiary, height: "40px", marginTop: "5px"}}>
          <NewButtonList
            contents={[
              t("animalPages.animal.feedCurve", {defaultValue: "Feed Curve"}),
              t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
            ]}
            onClick={(i) => {
              if (i === 0) {setCurrentFeedview("FEEDCURVE")}
              if (i === 1) {setCurrentFeedview("LAST10DAYS")}
            }}
            buttonType={ButtonTypes.textButton}
            listOverrides={{
              listGap: "5px"
            }}
            overrides={(index) => ({
              baseStyles: {
                backgroundColor: (currentFeedview === "FEEDCURVE" && index === 0) || (currentFeedview === "LAST10DAYS" && index === 1) ? colors.greyDarkPrimary : colors.transparent,
                padding: "8px 10px",
                borderRadius: "10px"
              },
              hoverStyles: {
                opacity: (currentFeedview === "FEEDCURVE" && index === 0) || (currentFeedview === "LAST10DAYS" && index === 1) ? 1 : 0.5,
              },
              contentStyles: {
                color: (currentFeedview === "FEEDCURVE" && index === 0) || (currentFeedview === "LAST10DAYS" && index === 1) ? colors.greyLightTertiary : colors.greyDarkPrimary,
              }
            })}
          />
        </div>
        <div style={{ marginTop: "10px" }}>
          {FeedviewStateRouter()}
        </div> 
      </> )
        
      // IF NO ANIMAL
      : (
        <p style={{ ...css.emptyPen, opacity: "0.5", marginBottom: "10px"}}>
          {t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}
        </p>
      )}
    </div>
  </> );
}

const RemainingToday = memo(({animalConsumption}: {animalConsumption?: Consumption[]}) => {
  const { t } = useTranslation();
  const consumptionToday = animalConsumption ? animalConsumption.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
  const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);

  return (
    <ProgressCircle
      total={consumptionTotalToday}
      progressed={consumptionProgressedToday}
      animation
      labels={{
        header: t("feed.general.remainingToday", { defaultValue: "remaining today" }),
        unit: <UNIT content={"KG"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>
      }}
      extension={{
        hasExtension: true
      }}
    />
  );
})

const RemainingYesterday = memo(({animalConsumption}: {animalConsumption?: Consumption[]}) => {
  const { t } = useTranslation();
  const consumptionYesterday = animalConsumption ? animalConsumption.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
  const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);

  return (
    <ProgressCircle
      total={consumptionTotalYesterday}
      progressed={consumptionProgressedYesterday}
      animation
      labels={{
        header: t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" }),
        unit: <UNIT content={"KG"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>
      }}
      extension={{
        hasExtension: true
      }}
    />
  );
})